/**
 * Temporary API wrapper for project-scoped endpoints.
 * These functions call the client directly with the new URL patterns.
 * After regenerating the API client (npm run generate-api), these can be
 * replaced with the auto-generated SDK functions.
 *
 * NOTE: Response types are double-wrapped ({ data: { data: X } }) because
 * @hey-api/client-fetch resolves `res.data` as `TData[keyof TData]` for Records.
 * The outer `{ data: ... }` unwraps to the inner `{ data: X }`, matching
 * the server's actual JSON envelope.
 */
import { client } from "./generated/client.gen";

// --- Auth ---

export const getAuthMe = () =>
  client.get<{ data: { data: { id: string; email: string; firstName: string | null; lastName: string | null; phoneNumber: string | null; phoneNumber2: string | null; timezone: string | null; role: string; organizationId: string; organizationName: string; onboardingCompleted: boolean } } }>({
    url: "/auth/me",
  });

export const patchAuthMe = (options: { body: { firstName?: string | null; lastName?: string | null; phoneNumber?: string | null; phoneNumber2?: string | null; timezone?: string | null } }) =>
  client.patch<{ data: { data: { id: string; email: string; firstName: string | null; lastName: string | null; phoneNumber: string | null; phoneNumber2: string | null; timezone: string | null } } }>({
    url: "/auth/me",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

export const deleteAuthMe = () =>
  client.delete<{ data: { data: { message: string } } }>({
    url: "/auth/me",
  });

export const putOnboardingComplete = (options: { body: { firstName: string; lastName: string; companyName: string; companySize: string; jobRole: string; referralSource: string } }) =>
  client.put<{ data: { data: { message: string } } }>({
    url: "/onboarding/complete",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

// --- Organizations ---

export interface Organization {
  id: string;
  name: string;
  role: string;
  createdAt: string;
}

export const getOrganizations = () =>
  client.get<{ data: { data: Organization[] } }>({
    url: "/organizations",
  });

export const postOrganizations = (options: { body: { name: string } }) =>
  client.post<{ data: { data: { id: string; name: string; createdAt: string } } }>({
    url: "/organizations",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

export const patchOrganization = (options: { path: { organizationId: string }; body: { name: string } }) =>
  client.patch<{ data: { data: { id: string; name: string; createdAt: string } } }>({
    url: "/organizations/{organizationId}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const postOrganizationsJoin = (options: { body: { token: string } }) =>
  client.post<{ data: { data: { message: string } } }>({
    url: "/organizations/join",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

// --- Organization Members ---

export interface OrgMember {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
}

export interface OrgInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export const getOrganizationMembers = () =>
  client.get<{ data: { data: OrgMember[] } }>({
    url: "/organization/members",
  });

export const putOrganizationMemberRole = (options: { path: { userId: string }; body: { role: string } }) =>
  client.put<{ data: { data: { message: string } } }>({
    url: "/organization/members/{userId}/role",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteOrganizationMember = (options: { path: { userId: string } }) =>
  client.delete<{ data: { data: { message: string } } }>({
    url: "/organization/members/{userId}",
    ...options,
  });

export const getOrganizationInvitations = () =>
  client.get<{ data: { data: OrgInvitation[] } }>({
    url: "/organization/invitations",
  });

export const postOrganizationInvitation = (options: { body: { email: string; role: string } }) =>
  client.post<{ data: { data: { message: string } } }>({
    url: "/organization/invitations",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

export const deleteOrganizationInvitation = (options: { path: { id: string } }) =>
  client.delete<{ data: { data: { message: string } } }>({
    url: "/organization/invitations/{id}",
    ...options,
  });

// --- Invitation (public) ---

export const getInvitationByToken = (options: { path: { token: string } }) =>
  client.get<{ data: { data: { email: string; organizationName: string; role: string } } }>({
    url: "/auth/invitation/{token}",
    ...options,
  });

export const postAcceptInvite = (options: { body: { token: string; password: string; firstName?: string; lastName?: string } }) =>
  client.post<{ data: { data: { message: string } } }>({
    url: "/auth/accept-invite",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

// --- Projects ---

export const getProjects = () =>
  client.get<{ data: { data: Array<{ id: string; organizationId: string; name: string; createdAt: string; updatedAt: string }> } }>({
    url: "/projects",
  });

export const postProjects = (options: { body: { name: string } }) =>
  client.post<{ data: { data: { id: string; organizationId: string; name: string; createdAt: string; updatedAt: string } } }>({
    url: "/projects",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

export const putProjectsByProjectId = (options: { path: { projectId: string }; body: { name: string } }) =>
  client.put<{ data: { data: { id: string; organizationId: string; name: string; createdAt: string; updatedAt: string } } }>({
    url: "/projects/{projectId}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteProjectsByProjectId = (options: { path: { projectId: string } }) =>
  client.delete<{ data: { message: string } }>({
    url: "/projects/{projectId}",
    ...options,
  });

// --- Monitors (project-scoped) ---

export const getProjectMonitors = (options: { path: { projectId: string }; query?: { page?: number; limit?: number; search?: string } }) =>
  client.get<{ data: { data: Array<{ id: string; projectId: string; name: string; url: string; type: string; status: string; intervalSeconds: number; regions: string[]; createdAt: string; updatedAt: string; maintenanceStartTime: string | null; maintenanceEndTime: string | null; maintenanceTimezone: string | null; maintenanceDays: string[] | null }>; pagination: { total: number; page: number; limit: number; totalPages: number } } }>({
    url: "/projects/{projectId}/monitors",
    ...options,
  });

export const postProjectMonitors = (options: { path: { projectId: string }; body: Record<string, unknown> }) =>
  client.post<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/monitors",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const getProjectMonitorById = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: { id: string; projectId: string; name: string; url: string; type: string; status: string; intervalSeconds: number; regions: string[]; createdAt: string; updatedAt: string; maintenanceStartTime: string | null; maintenanceEndTime: string | null; maintenanceTimezone: string | null; maintenanceDays: string[] | null } } }>({
    url: "/projects/{projectId}/monitors/{id}",
    ...options,
  });

export const putProjectMonitorById = (options: { path: { projectId: string; id: string }; body: Record<string, unknown> }) =>
  client.put<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/monitors/{id}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const postProjectMonitorTestAlert = (options: { path: { projectId: string; id: string } }) =>
  client.post<{ data: { data: { success: boolean } } }>({
    url: "/projects/{projectId}/monitors/{id}/test-alert",
    ...options,
  });

export const deleteProjectMonitorById = (options: { path: { projectId: string; id: string } }) =>
  client.delete<{ data: { message: string } }>({
    url: "/projects/{projectId}/monitors/{id}",
    ...options,
  });

export const getProjectMonitorSummary = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: { uptimeDurationSeconds: number; lastCheckedAt: string | null; incidentCount: number; latestStatus: string; latestReason: string; statusDurationSeconds: number } } }>({
    url: "/projects/{projectId}/monitors/{id}/summary",
    ...options,
  });

export const getProjectMonitorResponseTimes = (options: { path: { projectId: string; id: string }; query?: { period?: string; region?: string; mode?: string; resolution?: string } }) =>
  client.get<{ data: { data: { mode: "breakdown" | "quantiles"; series?: Array<{ timestamp: string; latencyMs: number; dnsMs: number; tcpMs: number; tlsMs: number; ttfbMs: number; transferMs: number; status: string; reason: string }>; quantileSeries?: Array<{ timestamp: string; p50: number; p95: number; p99: number; avg: number }>; regions: string[]; resolution: string | null } } }>({
    url: "/projects/{projectId}/monitors/{id}/response-times",
    ...options,
  });

export const getProjectMonitorAvailability = (options: { path: { projectId: string; id: string }; query?: { from?: string; to?: string } }) =>
  client.get<{ data: { data: { periods: Array<{ label: string; availability: number; downtimeSeconds: number; incidents: number; longestIncidentSeconds: number; avgIncidentSeconds: number; coverage: { observed: number; total: number; percent: number } }> } } }>({
    url: "/projects/{projectId}/monitors/{id}/availability",
    ...options,
  });

// --- Heartbeats (project-scoped) ---

export interface Heartbeat {
  id: string;
  projectId: string;
  name: string;
  token: string;
  status: string;
  period: number;
  periodUnit: string;
  gracePeriod: number;
  gracePeriodUnit: string;
  lastPingedAt: string | null;
  onCallUserId: string | null;
  notificationChannels: string[];
  escalationPolicy: string;
  createdAt: string;
  updatedAt: string;
}

export const getProjectHeartbeats = (options: { path: { projectId: string }; query?: { page?: number; limit?: number; search?: string } }) =>
  client.get<{ data: { data: Heartbeat[]; pagination: { total: number; page: number; limit: number; totalPages: number } } }>({
    url: "/projects/{projectId}/heartbeats",
    ...options,
  });

export const getProjectHeartbeatById = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: Heartbeat } }>({
    url: "/projects/{projectId}/heartbeats/{id}",
    ...options,
  });

export const postProjectHeartbeats = (options: { path: { projectId: string }; body: Record<string, unknown> }) =>
  client.post<{ data: { data: Heartbeat } }>({
    url: "/projects/{projectId}/heartbeats",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const putProjectHeartbeatById = (options: { path: { projectId: string; id: string }; body: Record<string, unknown> }) =>
  client.put<{ data: { data: Heartbeat } }>({
    url: "/projects/{projectId}/heartbeats/{id}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteProjectHeartbeatById = (options: { path: { projectId: string; id: string } }) =>
  client.delete<{ data: { message: string } }>({
    url: "/projects/{projectId}/heartbeats/{id}",
    ...options,
  });

export interface HeartbeatPing {
  id: string;
  heartbeatId: string;
  pingedAt: string;
}

export const getProjectHeartbeatPings = (options: { path: { projectId: string; id: string }; query?: { hours?: number } }) =>
  client.get<{ data: { data: HeartbeatPing[] } }>({
    url: "/projects/{projectId}/heartbeats/{id}/pings",
    ...options,
  });

// --- Incidents (project-scoped) ---

export const getProjectIncidents = (options: {
  path: { projectId: string };
  query?: {
    page?: number;
    limit?: number;
    status?: "open" | "acknowledged" | "resolved";
    cause?: "manual" | "automatic";
    monitorId?: string;
    heartbeatId?: string;
    startedAfter?: string;
    startedBefore?: string;
    search?: string;
  };
}) =>
  client.get<{
    data: {
      data: Array<Record<string, unknown>>;
      pagination: { total: number; page: number; limit: number; totalPages: number };
    };
  }>({
    url: "/projects/{projectId}/incidents",
    ...options,
  });

export const getProjectMonitorIncidents = (options: { path: { projectId: string; monitorId: string }; query?: { period?: string } }) =>
  client.get<{ data: { data: Array<Record<string, unknown>> } }>({
    url: "/projects/{projectId}/monitors/{monitorId}/incidents",
    ...options,
  });

export const postProjectMonitorIncidents = (options: { path: { projectId: string; monitorId: string }; body: Record<string, unknown> }) =>
  client.post<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/monitors/{monitorId}/incidents",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const postProjectIncidents = (options: { path: { projectId: string }; body: Record<string, unknown> }) =>
  client.post<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/incidents",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const getProjectMonitorIncidentById = (options: { path: { projectId: string; monitorId: string; id: string } }) =>
  client.get<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/monitors/{monitorId}/incidents/{id}",
    ...options,
  });

export const putProjectMonitorIncidentById = (options: { path: { projectId: string; monitorId: string; id: string }; body: Record<string, unknown> }) =>
  client.put<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/monitors/{monitorId}/incidents/{id}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteProjectMonitorIncidentById = (options: { path: { projectId: string; monitorId: string; id: string } }) =>
  client.delete<{ data: { message: string } }>({
    url: "/projects/{projectId}/monitors/{monitorId}/incidents/{id}",
    ...options,
  });

export const getProjectIncidentById = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/incidents/{id}",
    ...options,
  });

export const putProjectIncidentById = (options: { path: { projectId: string; id: string }; body: Record<string, unknown> }) =>
  client.put<{ data: { data: Record<string, unknown> } }>({
    url: "/projects/{projectId}/incidents/{id}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteProjectIncidentById = (options: { path: { projectId: string; id: string } }) =>
  client.delete<{ data: { message: string } }>({
    url: "/projects/{projectId}/incidents/{id}",
    ...options,
  });

export const getProjectMonitorIncidentComments = (options: { path: { projectId: string; monitorId: string; incidentId: string } }) =>
  client.get<{ data: { data: Array<{ id: string; incidentId: string; userId: string; userName: string | null; content: string; createdAt: string; updatedAt: string }> } }>({
    url: "/projects/{projectId}/monitors/{monitorId}/incidents/{incidentId}/comments",
    ...options,
  });

export const postProjectMonitorIncidentComments = (options: { path: { projectId: string; monitorId: string; incidentId: string }; body: { content: string } }) =>
  client.post<{ data: { data: { id: string; incidentId: string; userId: string; userName: string | null; content: string; createdAt: string; updatedAt: string } } }>({
    url: "/projects/{projectId}/monitors/{monitorId}/incidents/{incidentId}/comments",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const getProjectIncidentComments = (options: { path: { projectId: string; incidentId: string } }) =>
  client.get<{ data: { data: Array<{ id: string; incidentId: string; userId: string; userName: string | null; content: string; createdAt: string; updatedAt: string }> } }>({
    url: "/projects/{projectId}/incidents/{incidentId}/comments",
    ...options,
  });

export const postProjectIncidentComments = (options: { path: { projectId: string; incidentId: string }; body: { content: string } }) =>
  client.post<{ data: { data: { id: string; incidentId: string; userId: string; userName: string | null; content: string; createdAt: string; updatedAt: string } } }>({
    url: "/projects/{projectId}/incidents/{incidentId}/comments",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

// --- Status Pages ---

export interface NavLink {
  label: string;
  url: string;
}

export interface StatusPage {
  id: string;
  projectId: string;
  name: string;
  slug: string;
  description: string | null;
  isPublic: boolean;
  customDomain: string | null;
  websiteUrl: string | null;
  supportUrl: string | null;
  pageDesign: string;
  colorTheme: string;
  headerLayout: string;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  announcement: string | null;
  notifySubscribersOnAnnouncement: boolean;
  customCss: string | null;
  customJs: string | null;
  minIncidentLength: number;
  timezone: string;
  historyDays: number;
  showNotMonitoredAsGreen: boolean;
  uptimeDecimalPlaces: number;
  googleTagId: string | null;
  customNavLinks: NavLink[];
  hideFromSearchEngines: boolean;
  hideBranding: boolean;
  subscribingEnabled: boolean;
  autoStatusUpdates: boolean;
  passwordProtected: boolean;
  password: string | null;
  ipAllowlist: string | null;
  translations: Record<string, Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
}

export interface StatusPageMonitor {
  id: string;
  monitorId: string;
  sectionId: string | null;
  displayName: string | null;
  sortOrder: number;
  monitor: {
    id: string;
    name: string;
    url: string;
    type: string;
    status: string;
  };
}

export interface StatusPageWithMonitors extends StatusPage {
  monitors: StatusPageMonitor[];
}

export interface StatusPageSection {
  id: string;
  statusPageId?: string;
  name: string;
  sortOrder: number;
  createdAt?: string;
  monitors: StatusPageMonitor[];
}

export type StatusUpdateStatus = "investigating" | "identified" | "monitoring" | "resolved";

export type AffectedServiceStatus =
  | "not_affected"
  | "downtime"
  | "degraded"
  | "resolved";

export interface AffectedService {
  monitorId: string;
  displayName: string;
  status: AffectedServiceStatus;
  resolvedAt?: string | null;
}

export interface StatusPageUpdate {
  id: string;
  statusPageId: string;
  title: string | null;
  body: string;
  status: StatusUpdateStatus;
  publishedAt: string | null;
  affectedServices: AffectedService[];
  createdAt: string;
  updatedAt: string;
}

export interface StatusPageSubscriber {
  id: string;
  statusPageId: string;
  email: string;
  confirmedAt: string | null;
  createdAt: string;
}

export const getProjectStatusPages = (options: { path: { projectId: string } }) =>
  client.get<{ data: { data: StatusPage[] } }>({
    url: "/projects/{projectId}/status-pages",
    ...options,
  });

export const getProjectStatusPageById = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: StatusPageWithMonitors } }>({
    url: "/projects/{projectId}/status-pages/{id}",
    ...options,
  });

export const postProjectStatusPages = (options: { path: { projectId: string }; body: { name: string; slug: string; description?: string; isPublic?: boolean; customDomain?: string | null; websiteUrl?: string | null; supportUrl?: string | null; faviconUrl?: string | null; logoLightUrl?: string | null; logoDarkUrl?: string | null } }) =>
  client.post<{ data: { data: StatusPage } }>({
    url: "/projects/{projectId}/status-pages",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const postUpload = (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return client.post<{ data: { data: { url: string } } }>({
    url: "/uploads",
    body: formData,
    bodySerializer: null,
    headers: { "Content-Type": null },
  });
};


export const putProjectStatusPageById = (options: {
  path: { projectId: string; id: string };
  body: {
    name?: string;
    slug?: string;
    description?: string | null;
    isPublic?: boolean;
    customDomain?: string | null;
    websiteUrl?: string | null;
    supportUrl?: string | null;
    pageDesign?: string;
    colorTheme?: string;
    headerLayout?: string;
    logoLightUrl?: string | null;
    logoDarkUrl?: string | null;
    faviconUrl?: string | null;
    announcement?: string | null;
    notifySubscribersOnAnnouncement?: boolean;
    customCss?: string | null;
    customJs?: string | null;
    minIncidentLength?: number;
    timezone?: string;
    historyDays?: number;
    showNotMonitoredAsGreen?: boolean;
    uptimeDecimalPlaces?: number;
    googleTagId?: string | null;
    customNavLinks?: NavLink[];
    hideFromSearchEngines?: boolean;
    hideBranding?: boolean;
    subscribingEnabled?: boolean;
    autoStatusUpdates?: boolean;
    passwordProtected?: boolean;
    password?: string | null;
    ipAllowlist?: string | null;
    translations?: Record<string, Record<string, unknown>>;
  };
}) =>
  client.put<{ data: { data: StatusPage } }>({
    url: "/projects/{projectId}/status-pages/{id}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteProjectStatusPageById = (options: { path: { projectId: string; id: string } }) =>
  client.delete<{ data: { message: string } }>({
    url: "/projects/{projectId}/status-pages/{id}",
    ...options,
  });

export const putProjectStatusPageMonitors = (options: { path: { projectId: string; id: string }; body: { monitors: Array<{ monitorId: string; displayName?: string | null; sortOrder?: number }> } }) =>
  client.put<{ data: { data: StatusPageWithMonitors } }>({
    url: "/projects/{projectId}/status-pages/{id}/monitors",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

// --- Status Page Maintenances ---

export interface StatusPageMaintenance {
  id: string;
  statusPageId: string;
  title: string;
  description: string | null;
  scheduledStartAt: string;
  scheduledEndAt: string;
  status: "scheduled" | "in_progress" | "completed";
  createdAt: string;
  updatedAt: string;
}

export const getStatusPageMaintenances = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: StatusPageMaintenance[] } }>({
    url: "/projects/{projectId}/status-pages/{id}/maintenances",
    ...options,
  });

export const postStatusPageMaintenance = (options: {
  path: { projectId: string; id: string };
  body: {
    title: string;
    description?: string | null;
    scheduledStartAt: string;
    scheduledEndAt: string;
    status?: "scheduled" | "in_progress" | "completed";
  };
}) =>
  client.post<{ data: { data: StatusPageMaintenance } }>({
    url: "/projects/{projectId}/status-pages/{id}/maintenances",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const putStatusPageMaintenance = (options: {
  path: { projectId: string; id: string; maintenanceId: string };
  body: {
    title?: string;
    description?: string | null;
    scheduledStartAt?: string;
    scheduledEndAt?: string;
    status?: "scheduled" | "in_progress" | "completed";
  };
}) =>
  client.put<{ data: { data: StatusPageMaintenance } }>({
    url: "/projects/{projectId}/status-pages/{id}/maintenances/{maintenanceId}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteStatusPageMaintenance = (options: { path: { projectId: string; id: string; maintenanceId: string } }) =>
  client.delete<{ data: { data: { id: string } } }>({
    url: "/projects/{projectId}/status-pages/{id}/maintenances/{maintenanceId}",
    ...options,
  });

// --- Status Page Sections ---

export const getStatusPageSections = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: StatusPageSection[] } }>({
    url: "/projects/{projectId}/status-pages/{id}/sections",
    ...options,
  });

export const putStatusPageSections = (options: {
  path: { projectId: string; id: string };
  body: {
    sections: Array<{
      id?: string;
      name: string;
      sortOrder: number;
      monitors: Array<{ monitorId: string; displayName?: string | null; sortOrder?: number }>;
    }>;
    uncategorizedMonitors: Array<{ monitorId: string; displayName?: string | null; sortOrder?: number }>;
  };
}) =>
  client.put<{ data: { data: StatusPageSection[] } }>({
    url: "/projects/{projectId}/status-pages/{id}/sections",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

// --- Status Page Updates ---

export const getStatusPageUpdates = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: StatusPageUpdate[] } }>({
    url: "/projects/{projectId}/status-pages/{id}/updates",
    ...options,
  });

export const postStatusPageUpdate = (options: {
  path: { projectId: string; id: string };
  body: {
    title?: string | null;
    body: string;
    status?: StatusUpdateStatus;
    publishedAt?: string | null;
    affectedServices?: AffectedService[];
    notifySubscribers?: boolean;
  };
}) =>
  client.post<{ data: { data: StatusPageUpdate } }>({
    url: "/projects/{projectId}/status-pages/{id}/updates",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const putStatusPageUpdate = (options: {
  path: { projectId: string; id: string; updateId: string };
  body: {
    title?: string | null;
    body?: string;
    status?: StatusUpdateStatus;
    publishedAt?: string | null;
    affectedServices?: AffectedService[];
  };
}) =>
  client.put<{ data: { data: StatusPageUpdate } }>({
    url: "/projects/{projectId}/status-pages/{id}/updates/{updateId}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteStatusPageUpdate = (options: { path: { projectId: string; id: string; updateId: string } }) =>
  client.delete<{ data: { data: { id: string } } }>({
    url: "/projects/{projectId}/status-pages/{id}/updates/{updateId}",
    ...options,
  });

// --- Status Page Subscribers ---

export const getStatusPageSubscribers = (options: {
  path: { projectId: string; id: string };
  query?: { page?: number; limit?: number };
}) =>
  client.get<{ data: { data: StatusPageSubscriber[]; pagination: { total: number; page: number; limit: number; totalPages: number } } }>({
    url: "/projects/{projectId}/status-pages/{id}/subscribers",
    ...options,
  });

export const deleteStatusPageSubscriber = (options: { path: { projectId: string; id: string; subscriberId: string } }) =>
  client.delete<{ data: { data: { id: string } } }>({
    url: "/projects/{projectId}/status-pages/{id}/subscribers/{subscriberId}",
    ...options,
  });

// --- API Tokens ---

export interface ApiToken {
  id: string;
  name: string;
  tokenPrefix: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface ApiTokenCreated extends ApiToken {
  token: string;
}

export const getApiTokens = () =>
  client.get<{ data: { data: ApiToken[] } }>({
    url: "/api-tokens",
  });

export const postApiToken = (options: { body: { name: string; expiresAt?: string } }) =>
  client.post<{ data: { data: ApiTokenCreated } }>({
    url: "/api-tokens",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

export const deleteApiToken = (options: { path: { id: string } }) =>
  client.delete<{ data: { message: string } }>({
    url: "/api-tokens/{id}",
    ...options,
  });

// --- Billing ---

export interface BillingPlan {
  id: string;
  name: string;
  priceMonthly: number;
  priceAnnual: number;
  limits: {
    monitors: number;
    minCheckIntervalSeconds: number;
    statusPages: number;
    teamMembers: number;
    smsVoiceAlerts: boolean;
    incidentsAndOnCall: boolean;
    aiAnomalyDetection: boolean;
    playwrightChecks: boolean;
  };
}

export interface BillingSubscription {
  planId: string;
  planName: string;
  status: string;
  billingInterval: "monthly" | "annual";
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  limits: BillingPlan["limits"];
  usage: {
    monitors: number;
    statusPages: number;
    teamMembers: number;
  };
}

export const getBillingPlans = () =>
  client.get<{ data: { data: BillingPlan[] } }>({ url: "/billing/plans" });

export const getBillingSubscription = () =>
  client.get<{ data: { data: BillingSubscription } }>({ url: "/billing/subscription" });

export const postBillingCheckout = (options: { body: { planId: string; interval: "monthly" | "annual" } }) =>
  client.post<{ data: { data: { checkoutUrl: string } } }>({
    url: "/billing/checkout",
    body: options.body,
    headers: { "Content-Type": "application/json" },
  });

export const postBillingPortal = () =>
  client.post<{ data: { data: { portalUrl: string } } }>({ url: "/billing/portal" });

// --- Webhooks ---

export interface Webhook {
  id: string;
  organizationId: string;
  name: string;
  url: string;
  isActive: boolean;
  events: string[];
  hasSecret: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  incidentId: string | null;
  event: string;
  responseStatus: number | null;
  attempt: number;
  success: boolean;
  deliveredAt: string;
}

export const getWebhooks = () =>
  client.get<{ data: { data: Webhook[] } }>({ url: "/webhooks" });

export const postWebhooks = (options: { body: { name: string; url: string; secret?: string; events?: string[] } }) =>
  client.post<{ data: { data: Webhook } }>({
    url: "/webhooks",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const getWebhookById = (options: { path: { id: string } }) =>
  client.get<{ data: { data: Webhook } }>({ url: "/webhooks/{id}", ...options });

export const putWebhookById = (options: { path: { id: string }; body: { name?: string; url?: string; secret?: string | null; isActive?: boolean; events?: string[] } }) =>
  client.put<{ data: { data: Webhook } }>({
    url: "/webhooks/{id}",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const deleteWebhookById = (options: { path: { id: string } }) =>
  client.delete<{ data: { message: string } }>({ url: "/webhooks/{id}", ...options });

export const postWebhookTest = (options: { path: { id: string } }) =>
  client.post<{ data: { data: { success: boolean; responseStatus: number | null } } }>({
    url: "/webhooks/{id}/test",
    ...options,
  });

export const getWebhookDeliveries = (options: { path: { id: string }; query?: { limit?: number } }) =>
  client.get<{ data: { data: WebhookDelivery[] } }>({
    url: "/webhooks/{id}/deliveries",
    ...options,
  });

// --- Slack ---

export interface SlackIntegration {
  id: string;
  teamId: string;
  teamName: string;
  channelId: string;
  channelName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
}

export const getSlackOAuthUrl = () =>
  client.get<{ data: { data: { url: string } } }>({ url: "/oauth/slack" });

export const getSlackIntegration = () =>
  client.get<{ data: { data: SlackIntegration | null } }>({ url: "/slack/integration" });

export const deleteSlackIntegration = () =>
  client.delete<{ data: { message: string } }>({ url: "/slack/integration" });

export const patchSlackIntegration = (options: {
  body: { channelId?: string; channelName?: string; isActive?: boolean };
}) =>
  client.patch<{ data: { data: SlackIntegration } }>({
    url: "/slack/integration",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export const getSlackChannels = () =>
  client.get<{ data: { data: SlackChannel[] } }>({ url: "/slack/channels" });

export const postSlackTest = () =>
  client.post<{ data: { data: { success: boolean } } }>({ url: "/slack/integration/test" });

// --- DNS Verify & Cert State ---

export type DnsVerifyResult = {
  status: "ok" | "no_cname" | "wrong_target" | "nxdomain" | "timeout" | "error" | "rejected_internal" | "rejected_invalid";
  observed?: string;
  chain?: string[];
  expected: string;
  reason?: string;
};

export const postDnsVerify = (options: { path: { projectId: string }; body: { domain: string } }) =>
  client.post<{ data: { data: DnsVerifyResult } }>({
    url: "/projects/{projectId}/status-pages/dns-verify",
    ...options,
    headers: { "Content-Type": "application/json" },
  });

export type CertState = {
  state: "pending" | "issued" | "failed" | "renewing" | "released";
  reason?: string;
  lastIssuedAt?: string;
  lastRenewedAt?: string;
  lastAttemptAt?: string;
};

export const getCertState = (options: { path: { projectId: string; id: string } }) =>
  client.get<{ data: { data: CertState } }>({
    url: "/projects/{projectId}/status-pages/{id}/cert-state",
    ...options,
  });
